import api from './api'

export type OperatorMode = 'advisor' | 'operator'
export type OperatorImpactClass = 'low' | 'medium' | 'high' | 'always_escalate'
export type OperatorPolicyDecision = 'auto_apply' | 'requires_approval'
export type OperatorEvaluatedPolicyDecision =
  | 'record_observation'
  | 'auto_apply'
  | 'require_approval'
export type OperatorProposalState =
  | 'pending'
  | 'approved'
  | 'applied'
  | 'rejected'
  | 'superseded'
export type OperatorPackageState = 'applied' | 'partial' | 'failed' | 'denied'
export type OperatorZoom = 'day' | 'week' | 'month'

export type OperatorActionType =
  | 'record_observation'
  | 'create_project'
  | 'create_board'
  | 'create_task'
  | 'assign_owner'
  | 'set_due_date'
  | 'create_dependency'
  | 'merge_timeline_event'
  | 'change_project_deadline'
  | 'change_commercial_scope'
  | 'external_commitment'
  | 'staffing_conflict'

export type OperatorMutationActionType = Extract<
  OperatorActionType,
  | 'create_project'
  | 'create_board'
  | 'create_task'
  | 'assign_owner'
  | 'set_due_date'
  | 'create_dependency'
  | 'merge_timeline_event'
>

export type OperatorMutationTargetType =
  | 'crm_project'
  | 'taskboard_board'
  | 'taskboard_item'
  | 'taskboard_dependency'
  | 'operator_timeline_event'

export interface OperatorProposalEvidenceLinkDto {
  id: string
  workspaceId: string
  proposalId: string
  evidenceId: string
  createdAt: string
}

export interface OperatorProposalRecordDto {
  id: string
  workspaceId: string
  projectBindingId: string | null
  inferenceId: string | null
  proposedActionType: OperatorActionType
  targetType: OperatorMutationTargetType
  targetKey: string
  impactClass: OperatorImpactClass
  policyDecision: OperatorPolicyDecision
  confidence: number
  reason: string
  evidenceSetFingerprint: string | null
  proposalIdentity: string
  state: OperatorProposalState
  decidedByActorId: string | null
  decidedAt: string | null
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

/**
 * Exact JSON shape returned by Task 8 GET /operator/proposals.
 * Executable inputs intentionally do not live on this DTO.
 */
export interface OperatorProposalDto extends OperatorProposalRecordDto {
  evidenceLinks: OperatorProposalEvidenceLinkDto[]
}

export interface OperatorPolicyDto {
  mode: OperatorMode
  allowlistedActionTypes: OperatorActionType[]
  autoApplyThresholds: Partial<Record<OperatorActionType, number>>
}

export interface OperatorPolicyPatchDto {
  mode?: OperatorMode
  allowlistedActionTypes?: OperatorActionType[]
  autoApplyThresholds?: Partial<Record<OperatorActionType, number>>
  reason: string
}

export interface OperatorActionPolicyDto {
  mode: OperatorMode
  impactClass: OperatorImpactClass
  confidence: number
  isAuthorized: boolean
  allowlistedActionTypes: OperatorActionType[]
  autoApplyThresholds: Partial<Record<OperatorActionType, number>>
  escalationRequired?: boolean
}

export type OperatorBoardTargetDto =
  | { id: string }
  | { actionType: 'create_board'; targetKey: string }

export type OperatorItemTargetDto =
  | { id: string }
  | {
      actionType: 'create_task' | 'assign_owner' | 'set_due_date'
      targetKey: string
    }

interface OperatorPackageActionBaseDto<
  T extends OperatorMutationActionType,
  TTarget extends OperatorMutationTargetType,
> {
  actionType: T
  targetType: TTarget
  targetKey: string
  policy: OperatorActionPolicyDto
}

export interface OperatorCreateProjectActionDto
  extends OperatorPackageActionBaseDto<'create_project', 'crm_project'> {
  input: {
    name: string
    ownerUserId: string
    createdByUserId: string
    companyId?: string | null
    description?: string | null
    status?: string
    startedAt?: string | null
    targetGoLiveAt?: string | null
  }
}

export interface OperatorCreateBoardActionDto
  extends OperatorPackageActionBaseDto<'create_board', 'taskboard_board'> {
  input: {
    serverId: string
    name: string
    preset: 'jira_kanban' | 'monday_kanban' | 'monday_table' | 'custom'
    createdByUserId: string
    visibility?: 'personal' | 'department'
    departmentIds?: string[]
    icon?: string | null
    color?: string | null
  }
}

export interface OperatorCreateTaskActionDto
  extends OperatorPackageActionBaseDto<'create_task', 'taskboard_item'> {
  input: {
    board: OperatorBoardTargetDto
    title: string
    descriptionMd?: string | null
    statusId?: string
    assigneeUserId?: string | null
    reporterUserId: string
    dueDate?: string | null
    priority?: string | null
  }
}

export interface OperatorAssignOwnerActionDto
  extends OperatorPackageActionBaseDto<'assign_owner', 'taskboard_item'> {
  input: {
    item: OperatorItemTargetDto
    ownerUserId: string | null
  }
}

export interface OperatorSetDueDateActionDto
  extends OperatorPackageActionBaseDto<'set_due_date', 'taskboard_item'> {
  input: {
    item: OperatorItemTargetDto
    dueDate: string | null
  }
}

export interface OperatorCreateDependencyActionDto
  extends OperatorPackageActionBaseDto<
    'create_dependency',
    'taskboard_dependency'
  > {
  input: {
    fromItem: OperatorItemTargetDto
    toItem: OperatorItemTargetDto
    kind:
      | 'blocks'
      | 'is_blocked_by'
      | 'relates_to'
      | 'duplicates'
      | 'subtask_of'
  }
}

export interface OperatorMergeTimelineEventActionDto
  extends OperatorPackageActionBaseDto<
    'merge_timeline_event',
    'operator_timeline_event'
  > {
  input: {
    sourceEventId: string
    targetEventId: string
    evidenceIds: string[]
    targetEvidenceIds: string[]
    targetEvidenceAclSnapshot: Array<{
      evidenceId: string
      readerUserIds: string[]
    }>
    reason: string
    confidence: number
    impactClass: OperatorImpactClass
  }
}

export type OperatorPackageActionDto =
  | OperatorCreateProjectActionDto
  | OperatorCreateBoardActionDto
  | OperatorCreateTaskActionDto
  | OperatorAssignOwnerActionDto
  | OperatorSetDueDateActionDto
  | OperatorCreateDependencyActionDto
  | OperatorMergeTimelineEventActionDto

export type OperatorReviewActionDto = OperatorPackageActionDto & {
  mode: OperatorMode
  policyDecision: OperatorEvaluatedPolicyDecision
  policyReason: string
}

export interface OperatorReviewPackageDto {
  workspaceId: string
  proposalId: string
  projectBindingId: string | null
  actionPackageId: string
  packageFingerprint: string
  mode: OperatorMode
  actions: OperatorReviewActionDto[]
  executionState: OperatorPackageState | null
  actionOutcomes: OperatorPackageActionOutcomeDto[]
}

export interface OperatorPackageReferenceDto {
  actionPackageId: string
  packageFingerprint: string
}

export interface OperatorPackageActionOutcomeDto {
  actionId: string
  state: string
  retryable: boolean
}

export interface OperatorPackageActionResultDto
  extends OperatorPackageActionOutcomeDto {
  actionKey: string
  actionType: OperatorMutationActionType
  resultType: string | null
  resultId: string | null
  errorCategory: string | null
}

export interface OperatorPackageProjectionDto {
  workspaceId: string
  proposalId: string
  state: OperatorPackageState
  actions: OperatorPackageActionOutcomeDto[]
}

export interface OperatorPackageResultDto
  extends OperatorPackageProjectionDto {
  actions: OperatorPackageActionResultDto[]
}

export interface OperatorApprovalResultDto extends OperatorPackageResultDto {
  actionPackageId: string
}

export type OperatorEvidenceSourceType =
  | 'call'
  | 'spaces_session'
  | 'message'
  | 'email'
  | 'calendar'
  | 'crm'
  | 'taskboard'
  | 'sign'
  | 'document'

export interface OperatorAuthorizationSnapshotDto {
  workspaceId: string
  sourceAccountId: string
  authorizationBasis: string
  sourceChannelId?: string
  authorizedReaderUserIds?: string[]
}

export interface OperatorEvidenceInputDto {
  workspaceId?: string
  sourceType: OperatorEvidenceSourceType
  sourceId: string
  sourceRevision: string
  occurredAt: string
  authorizationSnapshot: OperatorAuthorizationSnapshotDto
  authorizedParticipantIds?: string[]
  observedParticipantIds?: string[]
  projectId?: string
  timecodeStartMs?: number
  timecodeEndMs?: number
  contentFingerprint?: string
  safeExcerpt?: string
  deepLink?: string
}

export interface OperatorEvidenceParticipantDto {
  id: string
  workspaceId: string
  evidenceId: string
  participantId: string
  createdAt: string
}

export interface OperatorPersistedEvidenceDto {
  id: string
  workspaceId: string
  projectBindingId: string | null
  sourceType: string
  sourceId: string
  sourceRevision: string
  occurredAt: string
  timecodeStartMs: number | null
  timecodeEndMs: number | null
  authorizationSnapshot: unknown
  contentFingerprint: string | null
  safeExcerpt: string | null
  deepLink: string | null
  ingestedAt: string
  authorizedParticipants: OperatorEvidenceParticipantDto[]
  observedParticipants: OperatorEvidenceParticipantDto[]
}

export interface OperatorEvidenceDto {
  id: string
  sourceType: string
  occurredAt: string
  timecodeStartMs: number | null
  timecodeEndMs: number | null
  safeExcerpt: string | null
  deepLink: string | null
  authorizationBasis: string
  sourceAccountId: string
  sourceChannelId: string | null
  observedParticipantIds: string[]
}

export interface OperatorInferenceProvenanceDto {
  id: string
  inferenceType: string
  modelIdentifier: string
  providerIdentifier: string
  promptKey: string
  promptRevision: string
  extractionVersion: string
  confidence: number
  reason: string
  linkedTasks: Array<{
    id: string
    deliverable: string
    linkedTaskboardItemId: string | null
  }>
  linkedMilestones: Array<{
    id: string
    title: string
  }>
  audit: Array<{
    id: string
    eventType: string
    detail: string | null
    occurredAt: string
  }>
}

interface OperatorEventEvidenceDrawerBaseDto {
  event: {
    id: string
    summary: string | null
  }
  visibleEvidenceCount: number
  withheldEvidenceCount: number
}

export interface OperatorAuthorizedEventEvidenceDrawerDto
  extends OperatorEventEvidenceDrawerBaseDto {
  fullyAuthorized: true
  evidence: OperatorEvidenceDto[]
  inferences: OperatorInferenceProvenanceDto[]
}

export interface OperatorPartialEventEvidenceDrawerDto
  extends OperatorEventEvidenceDrawerBaseDto {
  event: {
    id: string
    summary: null
  }
  fullyAuthorized: false
  evidence: []
  inferences: []
}

export type OperatorEventEvidenceDrawerDto =
  | OperatorAuthorizedEventEvidenceDrawerDto
  | OperatorPartialEventEvidenceDrawerDto

export interface OperatorProjectDto {
  id: string
  name: string
  client: string | null
  deadline: string | null
  health: 'on_track' | 'at_risk' | 'paused' | 'complete'
  visibleEvidenceCount: number
  withheldEvidenceCount: number
}

export interface OperatorProjectionPersonDto {
  id: string
  name: string
  role: string | null
}

export interface OperatorProjectionTeamDto {
  id: string
  name: string
  people: OperatorProjectionPersonDto[]
}

export interface OperatorProjectionPartyDto {
  id: string
  name: string
  role: string
  teams: OperatorProjectionTeamDto[]
  people: OperatorProjectionPersonDto[]
}

interface OperatorProjectionScopedDto {
  workspaceId: string
  projectBindingId: string
  deletedAt: string | null
}

export interface OperatorProjectionMilestoneDto
  extends OperatorProjectionScopedDto {
  id: string
  title: string
  baselinePlannedAt: string
  currentPlannedAt: string
  actualCompletedAt: string | null
  status: string
  ownerParticipantId: string | null
  slippageMs: number
  slippageDays: number
}

export interface OperatorProjectionCommitmentDto
  extends OperatorProjectionScopedDto {
  id: string
  deliverable: string
  dueAt: string | null
  status: string
  accountableParticipantId: string | null
  accountableOwnerId: string | null
  responsibleParticipantIds: string[]
  requestingPartyId?: string | null
  waitingPartyId?: string | null
  milestoneId?: string | null
  linkedTaskboardItemId?: string | null
}

export interface OperatorProjectionIntervalDto
  extends OperatorProjectionScopedDto {
  id: string
  title: string
  startedAt: string
  endedAt: string
  status: string
  accountableParticipantId: string | null
  responsibleParticipantIds: string[]
}

export interface OperatorProjectionEventDto extends OperatorProjectionScopedDto {
  id: string
  eventType: string
  startedAt: string
  endedAt: string | null
  observedStatus: string | null
  status: string | null
  accountableParticipantId: string | null
  accountableOwnerId: string | null
  title: string | null
  summary?: string | null
  responsibleParticipantIds: string[]
  participantIds: string[]
  evidenceCount: number
  visibleEvidenceCount: number
  withheldEvidenceCount: number
}

export interface OperatorProjectionDependencyDto
  extends OperatorProjectionScopedDto {
  id: string
  sourceCommitmentId?: string | null
  targetCommitmentId?: string | null
  sourceMilestoneId?: string | null
  targetMilestoneId?: string | null
  dependencyType: string
  type: string
  status: string
  linkedTaskboardDependencyId?: string | null
  source: { kind: 'commitment' | 'milestone'; id: string }
  target: { kind: 'commitment' | 'milestone'; id: string }
}

export type OperatorRiskCode =
  | 'OVERDUE_PROMISE'
  | 'DEPENDENCY_RISK'
  | 'DEADLINE_THREAT'
  | 'WAITING_ON'
  | 'MISSING_OWNER'
  | 'MISSING_DATE'

export interface OperatorProjectionRiskDto extends OperatorProjectionScopedDto {
  code: OperatorRiskCode
  severity: 'medium' | 'high' | 'critical'
  confidence: number
  reason: string
  evidenceIds: string[]
  commitmentId: string | null
  dependencyId: string | null
  nextBestAction: string
}

export interface OperatorProjectionFacetDto {
  value: string
  label: string
  count: number
}

export interface OperatorTimelineDto {
  projectId: string
  workspaceId: string
  revision: number
  hierarchy: OperatorProjectionPartyDto[]
  parties: OperatorProjectionPartyDto[]
  range: { start: string; end: string }
  today: string
  start: string | null
  deadline: string | null
  milestones: OperatorProjectionMilestoneDto[]
  commitments: OperatorProjectionCommitmentDto[]
  intervals: OperatorProjectionIntervalDto[]
  events: OperatorProjectionEventDto[]
  dependencies: OperatorProjectionDependencyDto[]
  risks: OperatorProjectionRiskDto[]
  visibleEvidenceCount: number
  withheldEvidenceCount: number
  filterFacets: {
    organizations: OperatorProjectionFacetDto[]
    teams: OperatorProjectionFacetDto[]
    people: OperatorProjectionFacetDto[]
    eventTypes: OperatorProjectionFacetDto[]
    statuses: OperatorProjectionFacetDto[]
    horizons: OperatorProjectionFacetDto[]
  }
}

export interface OperatorProposalQuery {
  state?: OperatorProposalState
  limit?: number
}

export const operatorApi = {
  ingestEvidence: (
    evidence: OperatorEvidenceInputDto,
    evidenceSignature?: string,
  ) => api.post<OperatorPersistedEvidenceDto>('/operator/evidence', evidence, {
    headers: evidenceSignature
      ? { 'X-Operator-Evidence-Signature': evidenceSignature }
      : undefined,
  }),
  listProposals: (params: OperatorProposalQuery = {}) =>
    api.get<OperatorProposalDto[]>('/operator/proposals', { params }),
  getProposalPackage: (proposalId: string) =>
    api.get<OperatorReviewPackageDto>(
      `/operator/proposals/${encodeURIComponent(proposalId)}/package`,
    ),
  approveProposal: (proposalId: string, body: OperatorPackageReferenceDto) =>
    api.post<OperatorApprovalResultDto>(
      `/operator/proposals/${encodeURIComponent(proposalId)}/approve`,
      body,
    ),
  rejectProposal: (proposalId: string) =>
    api.post<OperatorProposalDto>(
      `/operator/proposals/${encodeURIComponent(proposalId)}/reject`,
      {},
    ),
  getPolicy: () => api.get<OperatorPolicyDto>('/operator/policy'),
  patchPolicy: (patch: OperatorPolicyPatchDto) =>
    api.patch<OperatorPolicyDto>('/operator/policy', patch),
  listProjects: () =>
    api.get<OperatorProjectDto[]>('/operator/projects'),
  getProjectTimeline: (projectBindingId: string) =>
    api.get<OperatorTimelineDto>(
      `/operator/projects/${encodeURIComponent(projectBindingId)}/timeline`,
    ),
  getEventEvidence: (eventId: string) =>
    api.get<OperatorEventEvidenceDrawerDto>(
      `/operator/events/${encodeURIComponent(eventId)}/evidence`,
    ),
  retryAction: (actionId: string, body: OperatorPackageReferenceDto) =>
    api.post<OperatorPackageResultDto>(
      `/operator/actions/${encodeURIComponent(actionId)}/retry`,
      body,
    ),
}
